/**
 * 반 관리자
 * 반별 자리 배치도 저장/불러오기 기능 담당
 */

import { StorageManager } from './StorageManager.js';
import { OutputModule } from '../modules/OutputModule.js';
import { logger } from '../utils/logger.js';
import { Seat } from '../models/Seat.js';
import { Student } from '../models/Student.js';

/**
 * 반별 자리 배치도 데이터 타입
 */
export interface ClassLayoutData {
    seats: Seat[];
    students: Student[];
    timestamp: string;
    className: string;
}

/**
 * 반 정보 타입
 */
export interface ClassInfo {
    id: string;
    name: string;
    createdAt: string;
    lastModified: string;
}

/**
 * ClassManager가 필요로 하는 의존성 인터페이스
 */
export interface ClassManagerDependencies {
    storageManager: StorageManager;
    outputModule: OutputModule;
    getCurrentSeats: () => Seat[];
    getCurrentStudents: () => Student[];
    setSeats: (seats: Seat[]) => void;
    setStudents: (students: Student[]) => void;
    renderLayout: () => void;
}

/**
 * 반 관리자 클래스
 */
export class ClassManager {
    private deps: ClassManagerDependencies;
    private currentClassId: string | null = null;
    private readonly STORAGE_KEY_CLASSES = 'classList';
    private readonly STORAGE_KEY_PREFIX = 'classLayout_';

    constructor(dependencies: ClassManagerDependencies) {
        this.deps = dependencies;
    }

    /**
     * 반 목록 가져오기
     */
    public getClassList(): ClassInfo[] {
        return this.readClassList() ?? [];
    }

    /**
     * 반 목록 읽기 (엄격)
     * 읽기/파싱에 실패하면 null을 반환한다.
     * 빈 배열(반이 하나도 없음)과 읽기 실패를 반드시 구분해야 하는 곳에서 사용한다.
     * 이 둘을 혼동하면 읽기 실패 시 빈 목록을 클라우드에 덮어써서 전체 반 목록이 사라진다.
     */
    private readClassList(): ClassInfo[] | null {
        try {
            const read = this.deps.storageManager.readItem(this.STORAGE_KEY_CLASSES);
            if (!read.ok) {
                // 저장소 접근 자체가 실패한 경우 (없는 것과 구분해야 함)
                logger.error('반 목록 읽기 실패: 저장소에 접근할 수 없습니다.');
                return null;
            }
            if (!read.value) {
                // 아직 반이 하나도 없는 정상 상태
                return [];
            }

            const classList = JSON.parse(read.value) as ClassInfo[];
            return Array.isArray(classList) ? classList : null;
        } catch (error) {
            logger.error('반 목록 불러오기 중 오류:', error);
            return null;
        }
    }

    /**
     * 반 목록 저장하기
     */
    private async saveClassList(classList: ClassInfo[]): Promise<boolean> {
        try {
            return this.deps.storageManager.safeSetItem(
                this.STORAGE_KEY_CLASSES,
                JSON.stringify(classList)
            );
        } catch (error) {
            logger.error('반 목록 저장 중 오류:', error);
            this.deps.outputModule.showError('반 목록 저장 중 오류가 발생했습니다.');
            return false;
        }
    }

    /**
     * 새 반 추가
     */
    public async addClass(className: string): Promise<string | null> {
        if (!className || className.trim() === '') {
            this.deps.outputModule.showError('반 이름을 입력해주세요.');
            return null;
        }

        // 읽기에 실패했다면 절대 새 목록을 만들어 저장하면 안 된다.
        // 빈 목록으로 시작해 저장하면 클라우드의 기존 반 목록 전체를 덮어쓴다.
        const classList = this.readClassList();
        if (classList === null) {
            this.deps.outputModule.showError('반 목록을 읽을 수 없어 반을 추가할 수 없습니다. 브라우저 저장소 설정을 확인해주세요.');
            return null;
        }

        // 중복 확인
        const existingClass = classList.find(c => c.name === className.trim());
        if (existingClass) {
            this.deps.outputModule.showError('이미 존재하는 반 이름입니다.');
            return null;
        }

        const newClass: ClassInfo = {
            id: `class_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: className.trim(),
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString()
        };

        classList.push(newClass);
        
        const saved = await this.saveClassList(classList);
        if (saved) {
            this.deps.outputModule.showInfo(`"${className}" 반이 추가되었습니다.`);
            return newClass.id;
        }

        return null;
    }

    /**
     * 반 삭제
     */
    public async deleteClass(classId: string): Promise<boolean> {
        const classList = this.getClassList();
        const classIndex = classList.findIndex(c => c.id === classId);
        
        if (classIndex === -1) {
            this.deps.outputModule.showError('삭제할 반을 찾을 수 없습니다.');
            return false;
        }

        const className = classList[classIndex].name;

        // 목록에서 제거
        classList.splice(classIndex, 1);

        // 목록 저장을 먼저 수행한다.
        // 실제 배치도 데이터를 먼저 지우면, 목록 저장이 실패했을 때
        // 반은 목록에 남아 있는데 배치도만 사라진 상태가 된다.
        const saved = await this.saveClassList(classList);
        if (!saved) {
            return false;
        }

        // 목록이 저장된 뒤에 실제 데이터를 삭제한다.
        try {
            localStorage.removeItem(`${this.STORAGE_KEY_PREFIX}${classId}`);
        } catch (error) {
            logger.error('반 데이터 삭제 중 오류:', error);
        }

        // 현재 선택된 반이 삭제된 경우 선택 해제
        if (this.currentClassId === classId) {
            this.currentClassId = null;
        }

        this.deps.outputModule.showInfo(`"${className}" 반이 삭제되었습니다.`);
        return true;
    }

    /**
     * 현재 선택된 반 ID 가져오기
     */
    public getCurrentClassId(): string | null {
        return this.currentClassId;
    }

    /**
     * 반 선택
     */
    public selectClass(classId: string | null): void {
        this.currentClassId = classId;
    }

    /**
     * 현재 반의 자리 배치도 저장
     */
    public async saveCurrentLayout(options?: { silent?: boolean }): Promise<boolean> {
        const silent = options?.silent === true;

        if (!this.currentClassId) {
            this.deps.outputModule.showError('반을 선택해주세요.');
            return false;
        }

        try {
            const seats = this.deps.getCurrentSeats();
            const students = this.deps.getCurrentStudents();

            if (!seats || seats.length === 0 || !students || students.length === 0) {
                this.deps.outputModule.showError('저장할 자리 배치도가 없습니다.');
                return false;
            }

            // 저장할 시점의 상태를 복사해 둔다.
            // 라이브 참조를 그대로 직렬화하면 저장 도중의 변경이 섞여 들어간다.
            const layoutData: ClassLayoutData = {
                seats: JSON.parse(JSON.stringify(seats)) as Seat[],
                students: JSON.parse(JSON.stringify(students)) as Student[],
                timestamp: new Date().toISOString(),
                className: this.getClassList().find(c => c.id === this.currentClassId)?.name || ''
            };

            // localStorage에 저장
            const storageKey = `${this.STORAGE_KEY_PREFIX}${this.currentClassId}`;
            const localSuccess = this.deps.storageManager.safeSetItem(
                storageKey,
                JSON.stringify(layoutData)
            );

            if (localSuccess) {
                // 마지막 수정 시간 업데이트
                this.updateLastModified(this.currentClassId!);
                if (!silent) {
                    this.deps.outputModule.showInfo('자리 배치도가 저장되었습니다.');
                }
            }

            return localSuccess;
        } catch (error) {
            logger.error('자리 배치도 저장 중 오류:', error);
            this.deps.outputModule.showError('자리 배치도 저장 중 오류가 발생했습니다.');
            return false;
        }
    }

    /**
     * 선택된 반의 자리 배치도 불러오기
     */
    public async loadLayout(classId: string): Promise<boolean> {
        if (!classId) {
            return false;
        }

        try {
            let layoutData: ClassLayoutData | null = null;

            const storageKey = `${this.STORAGE_KEY_PREFIX}${classId}`;
            const layoutDataStr = this.deps.storageManager.safeGetItem(storageKey);

            if (!layoutDataStr) {
                this.deps.outputModule.showInfo('저장된 자리 배치도가 없습니다.');
                return false;
            }

            // JSON 파싱 시도 (데이터 손상 처리)
            try {
                layoutData = JSON.parse(layoutDataStr) as ClassLayoutData;
            } catch (parseError) {
                this.deps.outputModule.showError('저장된 데이터가 손상되어 불러올 수 없습니다.');
                logger.error('자리 배치도 파싱 오류:', parseError);
                return false;
            }

            // 데이터 구조 검증
            if (!layoutData || typeof layoutData !== 'object') {
                this.deps.outputModule.showError('저장된 데이터 형식이 올바르지 않습니다.');
                return false;
            }

            if (layoutData.seats && Array.isArray(layoutData.seats) && 
                layoutData.students && Array.isArray(layoutData.students)) {
                this.deps.setSeats(layoutData.seats);
                this.deps.setStudents(layoutData.students);
                
                // 레이아웃 렌더링
                this.deps.renderLayout();
                
                this.deps.outputModule.showInfo('자리 배치도를 불러왔습니다.');
                return true;
            } else {
                this.deps.outputModule.showError('저장된 데이터 형식이 올바르지 않습니다.');
                return false;
            }
        } catch (error) {
            logger.error('자리 배치도 불러오기 중 오류:', error);
            this.deps.outputModule.showError('자리 배치도 불러오기 중 오류가 발생했습니다.');
            return false;
        }
    }

    /**
     * 마지막 수정 시간 업데이트
     */
    private updateLastModified(classId: string): void {
        const classList = this.getClassList();
        const classInfo = classList.find(c => c.id === classId);
        
        if (classInfo) {
            classInfo.lastModified = new Date().toISOString();
            this.saveClassList(classList).catch((error) => {
                logger.error('마지막 수정 시간 저장 실패:', error);
            });
        }
    }

    /**
     * 반 이름 가져오기
     */
    public getClassName(classId: string): string | null {
        const classList = this.getClassList();
        const classInfo = classList.find(c => c.id === classId);
        return classInfo ? classInfo.name : null;
    }
}


